import { filter } from 'lodash'
import { i18n } from '@/i18n'

import uuidv4 from 'uuid/v4'

export default {
  state: {
    // Both regular and flash banners. Regular banners should persist and flash banners will disappear once the route
    // changes.
    banners: [],
    // Temporary notifications that fade out after a predetermined period of time. Notifications will be removed from
    // this array when the route changes, similar to flash banners. In the Vuetify UX these will be v-snackbars.
    notifications: [],
    // State for the GlobalModal component
    modal: undefined,
    // whether we are currently in maintenance mode.
    maintenanceMode: false
  },
  getters: {
    /**
     * @return string
     */
    getModal: state => {
      return state.modal
    },
    /**
     * @return array
     */
    getAllBanners: state => {
      return state.banners
    },
    /**
     * @return array
     */
    getNotifications: state => {
      return state.notifications
    },
    /**
     * @return boolean
     */
    maintenanceMode: state => {
      return state.maintenanceMode
    }
  },
  mutations: {
    /**
     * @param object state object injected by vuex
     * @param string message to be rendered in modal
     */
    setModal (state, modal) {
      state.modal = modal
    },

    /**
     * @param object state object injected by vuex
     * @param object banner object including message and other metadata
     */
    addBanner (state, banner) {
      state.banners.push(banner)
    },

    /**
     * @param object state object injected by vuex
     * @param object banner object
     */
    removeBanner (state, banner) {
      state.banners = filter(state.banners, (b) => {
        return banner !== b
      })
    },

    /**
     * Removes all banners from the state object
     *
     * @param object state object injected by vuex
     */
    clearAllBanners (state) {
      state.banners = []
    },

    /**
     * Removes all banners from the state object which have persist set to false
     *
     * @param object state object injected by vuex
     */
    clearFlashBanners (state) {
      state.banners = filter(state.banners, (banner) => {
        return banner.persist !== false
      })
    },

    /**
     * Add a notification to the state object.
     * @param state
     * @param notification
     */
    addNotification (state, notification) {
      state.notifications.push(notification)
    },
    /**
     * Removes all banners from the state object.
     * @param state
     */
    clearNotifications (state) {
      state.notifications = []
    },

    /**
     * Activates or deactivates maintenance mode.
     */
    setMaintenanceMode (state, mode) {
      state.maintenanceMode = mode
    }
  },
  actions: {
    /**
     * Sets the modal to be rendered.
     *
     * @param object context object injected by vuex
     * @param string message message to render in modal
     */
    showModal (context, modal) {
      context.commit('setModal', modal)
    },

    /**
     * Removes the modal
     *
     * @param object context object injected by vuex
     */
    hideModal (context) {
      context.commit('setModal', undefined)
    },

    /**
     * Sets the error message to be rendered in a modal requiring user
     * interaction to proceed.
     *
     * @param object context object injected by vuex
     * @param string message message to render in modal
     */
    showErrorModal (context, message) {
      const modal = {
        message: message,
        options: {
          title: i18n.t('error'),
          okOnly: true,
          okVariant: 'danger',
          centered: true
        }
      }

      context.commit('setModal', modal)
    },

    /**
     * Removes the error modal message
     *
     * @param object context object injected by vuex
     */
    hideErrorModal (context) {
      context.commit('setModal', undefined)
    },

    /**
     * Deletes the specified banner from the stack
     *
     * @param object context object injected by vuex
     * @param string message message to render in modal
     */
    removeBanner (context, banner) {
      context.commit('removeBanner', banner)
    },

    /**
     * Convenience function to create a success banner object given a string
     *
     * @param object context object injected by vuex
     * @param string msg to be displayed in banner
     */
    showSuccessBanner (context, msg) {
      const banner = {
        id: uuidv4().toString(),
        content: msg,
        persist: true,
        variant: 'success'
      }
      context.commit('addBanner', banner)
      return banner
    },

    /**
     * Convenience function to create an error banner object given a string
     *
     * @param object context object injected by vuex
     * @param string msg to be displayed in banner
     */
    showErrorBanner (context, msg) {
      const banner = {
        id: uuidv4().toString(),
        content: msg,
        persist: true,
        variant: 'danger'
      }
      context.commit('addBanner', banner)
      return banner
    },

    /**
     * Convenience function to create a warning banner object given a string
     *
     * @param object context object injected by vuex
     * @param string msg to be displayed in banner
     */
    showWarningBanner (context, msg) {
      const banner = {
        id: uuidv4().toString(),
        content: msg,
        persist: true,
        variant: 'warning'
      }
      context.commit('addBanner', banner)
      return banner
    },

    /**
     * Convenience function to create a success banner which is removed when the
     * route changes with the given a string
     *
     * @param object context object injected by vuex
     * @param string msg to be displayed in banner
     */
    showSuccessFlashBanner (context, msg) {
      const banner = {
        id: uuidv4().toString(),
        content: msg,
        persist: false,
        variant: 'success'
      }
      context.commit('addBanner', banner)
      return banner
    },

    /**
     * Convenience function to create a error banner which is removed when the
     * route changes with the given a string
     *
     * @param object context object injected by vuex
     * @param string msg to be displayed in banner
     */
    showErrorFlashBanner (context, msg) {
      const banner = {
        id: uuidv4().toString(),
        content: msg,
        persist: false,
        variant: 'danger'
      }
      context.commit('addBanner', banner)
      return banner
    },

    /**
     * Convenience function to create a warning banner which is removed when the
     * route changes with the given a string
     *
     * @param object context object injected by vuex
     * @param string msg to be displayed in banner
     */
    showWarningFlashBanner (context, msg) {
      const banner = {
        id: uuidv4().toString(),
        content: msg,
        persist: false,
        variant: 'warning'
      }
      context.commit('addBanner', banner)
      return banner
    },

    /**
     * Call the clearAllBanners mutation removing all banners from the stack
     *
     * @param {object} context object injected by vuex
     */
    clearAllBanners (context) {
      context.commit('clearAllBanners')
    },

    /**
     * Call the clearFlashBanners mutation removing all non-persistent banners
     * from the stack
     *
     * @param {object} context object injected by vuex
     */
    clearFlashBanners (context) {
      context.commit('clearFlashBanners')
    },

    /**
     * Create and show a success notification.
     * @param context
     * @param msg
     * @returns object
     */
    showSuccessNotification (context, msg) {
      const notification = {
        id: uuidv4().toString(),
        show: true,
        content: msg,
        timeout: 10 * 1000,
        color: 'success'
      }
      context.commit('addNotification', notification)
      return notification
    },

    /**
     * Call the clearNotifications mutation, clearing all notifications from the state.
     * @param context
     */
    clearNotifications (context) {
      context.commit('clearNotifications')
    },

    /**
     * Activates or deactives maintenance mode.
     * @param {boolean} mode
     */
    setMaintenanceMode (context, mode) {
      context.commit('setMaintenanceMode', mode)
    }

  }
}
